import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Linking, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { isDarkTheme } from '@/constants/Colors';
import { exportReferralImage } from '@/lib/exportReferralImage';
import { trackRetentionEvent, RETENTION_ACTION } from '@/constants/utils/analyticsEvents';
import { formatCreditBalance } from '@/lib/formatCredits';
import { DEFAULT_FONT_FAMILY } from '@/constants/config';
import { referralMessage, referralShareUrl, referralText } from '@/lib/referralSharing';

const TEMPLATES = ['personal', 'social', 'email'];
const CHANNELS = [
  { id: 'gab', label: 'Gab', icon: 'share' },
  { id: 'twitter', label: 'X', icon: 'share' },
  { id: 'email', label: 'Email', icon: 'envelope' },
  { id: 'sms', label: 'SMS', icon: 'message' },
];
const createStyles = isDark => {
  const ink = isDark ? '#F5F5F7' : '#1D1D1F';
  const secondary = isDark ? '#A1A1A6' : '#6E6E73';
  const surface = isDark ? '#202022' : '#F5F5F7';
  const paper = isDark ? '#151517' : '#FFFFFF';
  const line = isDark ? '#38383B' : '#E5E5E7';
  return StyleSheet.create({
    section: { gap: 22 },
    heading: { gap: 7 },
    title: { color: ink, fontSize: 24, lineHeight: 30, letterSpacing: -0.6 },
    secondary: { color: secondary, lineHeight: 20 },
    ink: { color: ink },
    columns: { gap: 26, alignItems: 'stretch' },
    column: { flex: 1, minWidth: 0, gap: 16 },
    tabs: { flexDirection: 'row', padding: 4, borderRadius: 12, backgroundColor: surface },
    tab: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 8, minHeight: 36, borderRadius: 9 },
    selectedTab: { backgroundColor: paper, shadowColor: '#000000', shadowOpacity: 0.06, shadowRadius: 3, shadowOffset: { width: 0, height: 1 } },
    editor: { gap: 14, padding: 18, borderRadius: 16, borderWidth: 1, borderColor: line, backgroundColor: paper },
    input: { color: ink, fontSize: 14, lineHeight: 22, minHeight: 156, padding: 0, textAlignVertical: 'top', ...(Platform.OS === 'web' ? { fontFamily: DEFAULT_FONT_FAMILY } : {}) },
    link: { color: secondary, fontSize: 12, lineHeight: 18 },
    row: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
    channel: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 44, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, borderColor: line, flexGrow: 1 },
    action: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', minHeight: 46, paddingHorizontal: 18, gap: 8, borderRadius: 12, backgroundColor: ink },
    actionLabel: { color: isDark ? '#1D1D1F' : '#FFFFFF' },
    secondaryAction: { backgroundColor: paper, borderWidth: 1, borderColor: line },
    card: { backgroundColor: '#F5F5F7', borderRadius: 18, padding: 26, gap: 24, minHeight: 390, overflow: 'hidden' },
    brand: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    logo: { width: 26, height: 26 },
    cardBrand: { color: '#1D1D1F', fontSize: 16 },
    cardHeading: { color: '#1D1D1F', fontSize: 29, lineHeight: 34, letterSpacing: -0.9 },
    cardBody: { color: '#6E6E73', fontSize: 13, lineHeight: 20 },
    cardReward: { color: '#1D1D1F', fontSize: 25, lineHeight: 31, letterSpacing: -0.6 },
    cardFooter: { gap: 8, borderTopWidth: 1, borderColor: '#D8D8DC', paddingTop: 18 },
    cardLink: { color: '#1D1D1F', fontSize: 12, lineHeight: 19, ...(Platform.OS === 'web' ? { overflowWrap: 'anywhere' } : {}) },
    tip: { flexDirection: 'row', alignItems: 'flex-start', gap: 9, paddingHorizontal: 2 },
  });
};

function KitAction({ label, onPress, disabled, busy, secondary, icon, styles }) {
  const ink = secondary ? styles.ink.color : styles.actionLabel.color;
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={label}
      accessibilityState={{ disabled: !!disabled, busy: !!busy }} disabled={disabled}
      onPress={onPress} style={({ pressed }) => [styles.action, secondary && styles.secondaryAction, (pressed || disabled) && { opacity: 0.55 }]}>
      {busy ? <ActivityIndicator size="small" color={ink} /> : <Icon name={icon} size={14} color={ink} />}
      <Text size="s" weight="semibold" style={{ color: ink }}>{label}</Text>
    </Pressable>
  );
}

export default function ReferralShareKit({ referralLink, creditsPerReferral, rewardAvailable = true }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(isDarkTheme(theme)), [theme]);
  const [wide, setWide] = useState(false);
  const [template, setTemplate] = useState('personal');
  const [drafts, setDrafts] = useState({});
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const cardRef = useRef(null);
  const copiedTimer = useRef(null);
  const savingRef = useRef(false);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const credits = formatCreditBalance(creditsPerReferral);
  const draftKey = `${template}:${rewardAvailable}:${credits}:${t('settings.referral.kitTitle')}`;
  const draft = drafts[draftKey] ?? referralText(t, `${rewardAvailable ? 'templates' : 'generalTemplates'}.${template}`, { CREDITS: credits });
  const message = referralMessage(draft, referralLink);
  const canShare = Boolean(referralLink && draft.trim());

  const copyMessage = async () => {
    if (!canShare) return;
    setError('');
    try {
      await Clipboard.setStringAsync(message);
      trackRetentionEvent(RETENTION_ACTION.REFERRAL_SHARED, `copy_message_${template}`);
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2200);
    } catch {
      setError(t('settings.referral.failedToCopyMessage'));
    }
  };

  const shareMessage = async channel => {
    if (!canShare) return;
    setError('');
    try {
      const url = referralShareUrl(channel, message, t('settings.referral.emailSubject'), Platform.OS);
      if (!url) return;
      await Linking.openURL(url);
      trackRetentionEvent(RETENTION_ACTION.REFERRAL_SHARED, `${channel}_${template}`);
    } catch {
      setError(t('settings.referral.failedToShare'));
    }
  };

  const saveImage = async () => {
    if (!referralLink || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      await exportReferralImage(cardRef.current, {
        headline: t('settings.referral.imageHeadline'),
        benefits: t('settings.referral.imageBenefits'),
        reward: rewardAvailable ? referralText(t, 'imageReward', { CREDITS: credits }) : null,
        condition: t('settings.referral.imageCondition'),
        linkLabel: t('settings.referral.imageLinkLabel'),
        link: referralLink,
        disclosure: t('settings.referral.imageDisclosure'),
      });
    } catch {
      setError(t('settings.referral.failedToSaveImage'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <View style={styles.section} onLayout={event => setWide(event.nativeEvent.layout.width >= 680)}>
      <View style={styles.heading}>
        <Text weight="bold" style={styles.title} accessibilityRole="header" accessibilityLevel={2}>{t('settings.referral.kitTitle')}</Text>
        <Text size="s" style={styles.secondary}>{t('settings.referral.kitDescription')}</Text>
      </View>
      <View style={[styles.columns, { flexDirection: wide ? 'row' : 'column' }]}>
        <View style={styles.column}>
          <View style={styles.tabs}>
            {TEMPLATES.map(id => (
              <Pressable key={id} accessibilityRole="button" accessibilityState={{ selected: id === template }}
                onPress={() => { setTemplate(id); setCopied(false); setError(''); }}
                style={[styles.tab, id === template && styles.selectedTab]}>
                <Text size="xs" style={styles.ink} weight={id === template ? 'semibold' : 'normal'}>{t(`settings.referral.templateLabels.${id}`)}</Text>
              </Pressable>
            ))}
          </View>
          <View style={styles.editor}>
            <TextInput accessibilityLabel={t('settings.referral.messageLabel')}
              multiline value={draft} maxLength={2000} style={styles.input}
              onChangeText={value => { setDrafts(previous => ({ ...previous, [draftKey]: value })); setCopied(false); }} />
            <Text size="xs" style={styles.secondary}>{t('settings.referral.linkIncluded')}</Text>
            <Text selectable style={styles.link}>{referralLink}</Text>
          </View>
          <KitAction label={copied ? t('settings.referral.copied') : t('settings.referral.copyMessage')}
            icon={copied ? 'check' : 'copy'} onPress={copyMessage} disabled={!canShare} styles={styles} />
          <View style={styles.row}>
            {CHANNELS.map(channel => (
              <Pressable key={channel.id} accessibilityRole="button"
                accessibilityLabel={referralText(t, 'shareOn', { CHANNEL: channel.label })}
                accessibilityState={{ disabled: !canShare }} disabled={!canShare}
                onPress={() => shareMessage(channel.id)}
                style={({ pressed }) => [styles.channel, pressed && { opacity: 0.55 }]}>
                <Icon name={channel.icon} size={12} color={styles.secondary.color} />
                <Text size="xs" style={styles.ink}>{channel.label}</Text>
              </Pressable>
            ))}
          </View>
          <View style={styles.tip}>
            <Icon name="lightbulb" size={13} color={styles.secondary.color} />
            <Text size="xs" style={[styles.secondary, { flex: 1 }]}>{t('settings.referral.sharingTip')}</Text>
          </View>
        </View>
        <View style={styles.column}>
          <View ref={cardRef} collapsable={false} dataSet={{ cardCapture: '' }} style={styles.card}>
            <View style={styles.brand}>
              <Image source={require('../../public/brand-assets/gab-ai-mark-black.png')} style={styles.logo} accessible={false} />
              <Text weight="bold" style={styles.cardBrand}>Gab AI</Text>
            </View>
            <View style={{ gap: 12 }}>
              <Text weight="bold" style={styles.cardHeading}>{t('settings.referral.imageHeadline')}</Text>
              <Text style={styles.cardBody}>{t('settings.referral.imageBenefits')}</Text>
            </View>
            {rewardAvailable && (
              <View style={{ gap: 6 }}>
                <Text weight="bold" style={styles.cardReward}>{referralText(t, 'imageReward', { CREDITS: credits })}</Text>
                <Text style={styles.cardBody}>{t('settings.referral.imageCondition')}</Text>
              </View>
            )}
            <View style={styles.cardFooter}>
              <Text size="xs" weight="semibold" style={{ color: '#1D1D1F' }}>{t('settings.referral.imageLinkLabel')}</Text>
              <Text style={styles.cardLink}>{referralLink}</Text>
              {rewardAvailable && <Text size="xxs" style={{ color: '#6E6E73', lineHeight: 17 }}>{t('settings.referral.imageDisclosure')}</Text>}
            </View>
          </View>
          <KitAction label={t(Platform.OS === 'web' ? 'settings.referral.downloadImage' : 'settings.referral.saveImage')}
            icon="download" secondary onPress={saveImage} busy={saving} disabled={!referralLink || saving} styles={styles} />
          <Text size="xs" style={styles.secondary}>{t('settings.referral.imageDescription')}</Text>
        </View>
      </View>
      {!!error && <Text size="s" color="danger" accessibilityRole="alert">{error}</Text>}
    </View>
  );
}
